
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useBiddingStore from "../useBiddingStore";

const STATUS_TITLES = {
  all: "All bids",
  live: "Live bids",
  upcoming: "Upcoming bids",
  awarded: "Awarded bids",
  submitted: "Submitted bids",
  previous: "Previous bids",
};

const PAGE_SIZE = 50;

export default function useBidsListLogic() {
  const { status } = useParams();
  const navigate = useNavigate();

  const activeTab = status || "live";

  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    biddingSessions,
    biddingSessionsStatus,
    biddingSessionsError,
    hasNextBiddingSessionsPage,
    getBiddingSessions,
  } = useBiddingStore();

  useEffect(() => {
    setPageNumber(1);
    setHasNextPage(false);
    getBiddingSessions(activeTab, 1, PAGE_SIZE).then((result) => {
      if (result) setHasNextPage(result.hasNextPage);
    });
  }, [activeTab]);

  useEffect(() => {
    if (pageNumber === 1) return;
    getBiddingSessions(activeTab, pageNumber, PAGE_SIZE).then((result) => {
      if (result) setHasNextPage(result.hasNextPage);
    });
  }, [pageNumber]);

  // Keep local hasNextPage in sync with store (handles race-condition guard returning null)
  useEffect(() => {
    setHasNextPage(hasNextBiddingSessionsPage);
  }, [hasNextBiddingSessionsPage]);

  const handleTabChange = useCallback(
    (tab) => {
      setPageNumber(1);
      navigate(tab === "all" ? "/bids/list/all" : `/bids/${tab}`);
    },
    [navigate]
  );

  const goToPage = useCallback(
    (page) => {
      setPageNumber(page);
    },
    []
  );

  const toggleSearch = () => {
    if (showSearch) {
      setSearchQuery("");
    }
    setShowSearch((s) => !s);
  };

  const title = STATUS_TITLES[activeTab] || "Bids";

  const filtered = searchQuery
    ? biddingSessions.filter(
        (b) =>
          b.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          b.bidId?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : biddingSessions;

  const isInitialLoading = biddingSessionsStatus === "BEGIN";
  const hasError = biddingSessionsStatus === "FAIL";

  const bidsTableRowData = biddingSessions;

  return {
    // routing
    status: activeTab,
    activeTab,
    navigate,
    // search (mobile)
    showSearch,
    toggleSearch,
    searchQuery,
    setSearchQuery,
    // data
    biddingSessions,
    biddingSessionsStatus,
    biddingSessionsError,
    // derived
    title,
    filtered,
    isInitialLoading,
    hasError,
    // desktop table
    bidsTableRowData,
    pageNumber,
    hasNextPage,
    // handlers
    handleTabChange,
    goToPage,
  };
}
